import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trophy, Coins, Crown, Medal, Timer, Sparkles, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useWeeklyLeaderboard, WEEKLY_PRIZES } from '@/hooks/useWeeklyLeaderboard';
import { useAuth } from '@/hooks/useAuth';

export default function WeeklyLeaderboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { 
    leaderboard, 
    userStats, 
    previousWinners, 
    loading, 
    getTimeUntilReset,
    getPrizeForRank,
    refreshLeaderboard 
  } = useWeeklyLeaderboard();

  const [countdown, setCountdown] = useState(getTimeUntilReset());

  // Update countdown every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(getTimeUntilReset());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1: return <Crown className="h-6 w-6 text-yellow-500" />;
      case 2: return <Medal className="h-6 w-6 text-gray-400" />;
      case 3: return <Medal className="h-6 w-6 text-amber-600" />;
      default: return <span className="font-bold text-muted-foreground">#{rank}</span>;
    }
  };

  const getRankBg = (rank: number) => {
    switch (rank) {
      case 1: return 'bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border-yellow-500/50';
      case 2: return 'bg-gradient-to-r from-gray-400/20 to-gray-500/20 border-gray-400/50';
      case 3: return 'bg-gradient-to-r from-amber-600/20 to-orange-600/20 border-amber-600/50';
      default: return '';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Trophy className="h-6 w-6 text-yellow-500" />
              <span className="bg-gradient-to-r from-yellow-500 to-amber-500 bg-clip-text text-transparent">
                Weekly Challenge
              </span>
            </h1>
            <p className="text-muted-foreground text-sm">Earn the most coins to win prizes!</p>
          </div>
        </div>

        {/* Countdown Timer */}
        <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Timer className="h-8 w-8 text-purple-500" />
                <div>
                  <p className="text-sm text-muted-foreground">Resets in</p>
                  <p className="text-2xl font-bold">
                    {countdown.days}d {countdown.hours}h {countdown.minutes}m {countdown.seconds}s
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={refreshLeaderboard}>
                <TrendingUp className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Prize Tiers */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-yellow-500" />
              Prize Pool
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
              {WEEKLY_PRIZES.slice(0, 5).map((prize, i) => (
                <div 
                  key={i}
                  className={`p-3 rounded-lg text-center ${
                    i === 0 ? 'bg-yellow-500/20 border border-yellow-500/50' :
                    i === 1 ? 'bg-gray-400/20 border border-gray-400/50' :
                    i === 2 ? 'bg-amber-600/20 border border-amber-600/50' :
                    'bg-muted/50'
                  }`}
                >
                  <p className="text-xs font-medium mb-1">{prize.label}</p>
                  <div className="flex items-center justify-center gap-1 text-yellow-500">
                    <Coins className="h-3 w-3" />
                    <span className="font-bold">{prize.coins}</span>
                  </div>
                  {prize.gems > 0 && (
                    <p className="text-xs text-blue-500">+{prize.gems} gems</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Your Stats */}
        {user && userStats && (
          <Card className="border-primary/50 bg-primary/5">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-primary/20">
                    <Trophy className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Your Rank</p>
                    <p className="text-xl font-bold">
                      {userStats.rank > 0 ? `#${userStats.rank}` : 'Unranked'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Coins Earned</p>
                  <div className="flex items-center gap-1 text-yellow-500">
                    <Coins className="h-5 w-5" />
                    <span className="text-xl font-bold">{userStats.coinsEarned.toLocaleString()}</span>
                  </div>
                </div>
              </div>
              {userStats.rank > 0 && userStats.rank <= 10 && (
                <div className="mt-3 p-2 rounded bg-green-500/20 text-green-500 text-sm text-center">
                  🎉 You're in the prize zone! Keep it up!
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Leaderboard */}
        <Card>
          <CardHeader>
            <CardTitle>Top Earners This Week</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              <div className="space-y-2">
                {loading ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <Skeleton key={i} className="h-14 w-full" />
                  ))
                ) : leaderboard.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Trophy className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No entries yet this week</p>
                    <p className="text-sm">Be the first to earn coins!</p>
                  </div>
                ) : (
                  leaderboard.map((entry) => {
                    const isCurrentUser = user?.id === entry.user_id;
                    const prize = getPrizeForRank(entry.rank);
                    
                    return (
                      <div
                        key={entry.id}
                        className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                          isCurrentUser ? 'bg-primary/10 border-primary/50' : getRankBg(entry.rank) || 'bg-muted/30 border-transparent hover:bg-muted/50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 flex justify-center">
                            {getRankIcon(entry.rank)}
                          </div>
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={entry.avatar_url || undefined} />
                            <AvatarFallback>
                              {(entry.display_name || entry.username || '?')[0].toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">
                              {entry.display_name || entry.username || 'Anonymous'}
                              {isCurrentUser && <Badge variant="outline" className="ml-2 text-xs">You</Badge>}
                            </p>
                            {prize && (
                              <p className="text-xs text-muted-foreground">
                                Prize: {prize.coins} coins{prize.gems > 0 && ` + ${prize.gems} gems`}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-yellow-500">
                          <Coins className="h-5 w-5" />
                          <span className="font-bold">{entry.coins_earned.toLocaleString()}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Previous Week Winners */}
        {previousWinners.length > 0 && (
          <Card className="bg-muted/30">
            <CardHeader>
              <CardTitle className="text-lg">Last Week's Winners</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex justify-center gap-4">
                {previousWinners.map((winner) => (
                  <div key={winner.id} className="text-center">
                    <div className="mb-2">{getRankIcon(winner.rank)}</div>
                    <Avatar className="h-12 w-12 mx-auto mb-1">
                      <AvatarImage src={winner.avatar_url || undefined} />
                      <AvatarFallback>
                        {(winner.display_name || winner.username || '?')[0].toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <p className="font-medium text-sm">{winner.display_name || winner.username}</p>
                    <p className="text-xs text-yellow-500">{winner.coins_earned.toLocaleString()} coins</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* How to Earn */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">How to Earn Coins</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center text-sm">
              <div className="p-3 rounded-lg bg-muted/50">
                <div className="text-2xl mb-1">🤖</div>
                <p className="font-medium">Beat Bots</p>
                <p className="text-muted-foreground text-xs">10-50 coins</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <div className="text-2xl mb-1">📅</div>
                <p className="font-medium">Daily Bonus</p>
                <p className="text-muted-foreground text-xs">25-175 coins</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <div className="text-2xl mb-1">🧩</div>
                <p className="font-medium">Solve Puzzles</p>
                <p className="text-muted-foreground text-xs">5-20 coins</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <div className="text-2xl mb-1">🏆</div>
                <p className="font-medium">Win Games</p>
                <p className="text-muted-foreground text-xs">20-100 coins</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
